// /staff — Staff Performance Dashboard (PAGES.md §3.106)
//
// Staff activity and sales performance dashboard (API §36, PDF p49).
// Shows all staff members with their sales metrics, activity summaries,
// and performance indicators. Drill into individual staff for detailed
// activity logs, hourly performance, and top products sold.
// SHARED vertical. No feature flag. Min role: MANAGER (30) view, OWNER (50) commission.
// Traceability: FR-STAFF-001

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Badge,
  Select,
  DatePicker,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  DataTable,
  Pagination,
  Modal,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ROLE_LEVELS, ROLE_BADGE_VARIANT } from '@/types/user';
import { useAuthStore } from '@/stores/use-auth-store';
import { staffService } from '@/services/staff.service';
import type {
  StaffPerformance,
  StaffPerformanceRow,
  StaffPerformanceListResponse,
  StaffActivityEntry,
} from '@/services/staff.service';
import { ApiError } from '@/lib/api-client';
import { GLASS, GLASS_FILTER, GLASS_INTERACTIVE, cn, toneClasses } from '@/constants/design-tokens';
import { toLocalIsoDate } from './date-utils';

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const ROLE_FILTER_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'OWNER', label: 'Owner' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'CASHIER', label: 'Cashier' },
  { value: 'CAPTAIN', label: 'Captain' },
];

// ─── Icons ─────────────────────────────────────────────────────────────────

function UsersIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function UsersEmptyIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function TrophyIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9H4.5a2.5 2.5 0 010-5H6M18 9h1.5a2.5 2.5 0 000-5H18M6 9v5a6 6 0 0012 0V9M6 9h12M9 21h6M12 17v4" />
    </svg>
  );
}

function ChartBarIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20V10M18 20V4M6 20v-4" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function getMonthRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: toLocalIsoDate(start),
    end: toLocalIsoDate(end),
  };
}

// Returns true when the From/To range is inverted (start later than end).
// ISO yyyy-mm-dd strings compare lexicographically in chronological order.
export function isInvalidDateRange(start: string, end: string): boolean {
  return Boolean(start) && Boolean(end) && start > end;
}

function initials(name: string): string {
  return name.split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
}

export function performanceLevel(
  sales: number,
  avgSales: number,
): 'TOP' | 'AVERAGE' | 'LOW' {
  if (avgSales === 0) return 'AVERAGE';
  if (sales >= avgSales * 1.3) return 'TOP';
  if (sales < avgSales * 0.7) return 'LOW';
  return 'AVERAGE';
}

// Tenant-wide average sales per active staffer, used as the Top/Average/Low
// badge threshold. Must be derived from the tenant-wide summary (NOT the
// visible page) so a staffer's badge reflects whole-team standing and does not
// flip as you paginate or filter. See issue #189.
export function teamAvgSalesPerStaff(totalSales: number, activeStaff: number): number {
  return activeStaff > 0 ? totalSales / activeStaff : 0;
}

const PERF_BADGE: Record<string, { variant: 'success' | 'warning' | 'danger'; label: string }> = {
  TOP: { variant: 'success', label: 'Top' },
  AVERAGE: { variant: 'warning', label: 'Average' },
  LOW: { variant: 'danger', label: 'Low' },
};

// ─── Hourly Bar Chart (div-based) ──────────────────────────────────────────

// Collapse the API rows into a 24-slot revenue array. The backend may emit
// more than one row per hour (e.g. per-day or per-location buckets when the
// range spans a month), so every entry for an hour must be SUMMED — picking
// the first match (.find) would silently undercount the bar. See issue #188.
export function bucketRevenueByHour(
  data: Array<{ hour: number; revenue: number }>,
): number[] {
  const buckets = new Array<number>(24).fill(0);
  for (const { hour, revenue } of data) {
    if (hour >= 0 && hour < 24) buckets[hour] += revenue;
  }
  return buckets;
}

function HourlyChart({ data }: { data: Array<{ hour: number; revenue: number }> }) {
  const currency = useTenantCurrency();
  const hourly = bucketRevenueByHour(data);
  const maxRevenue = Math.max(...hourly, 1);
  return (
    <div className="flex items-end gap-1 h-40" role="img" aria-label="Hourly performance chart">
      {Array.from({ length: 24 }, (_, h) => {
        const revenue = hourly[h];
        const heightPct = (revenue / maxRevenue) * 100;
        return (
          <div key={h} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full relative" style={{ height: '128px' }}>
              <div
                className={cn(
                  'absolute bottom-0 w-full rounded-t-sm transition-all duration-300',
                  revenue > 0
                    ? 'bg-gradient-to-t from-violet-500 to-pink-400'
                    : 'bg-neutral-200 dark:bg-neutral-700',
                )}
                style={{ height: `${Math.max(heightPct, 2)}%` }}
                title={`${h}:00 — ${formatPaise(revenue, currency)}`}
              />
            </div>
            {h % 3 === 0 && (
              <span className="text-[9px] font-mono text-neutral-400 dark:text-neutral-500 tabular-nums">
                {h}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Staff Performance Row (DataTable row) ─────────────────────────────────
// Each row comes pre-aggregated from the server's /staff/performance endpoint.
// DataTable requires each row to have an `id` field; we alias `user_id`.

interface StaffRow extends StaffPerformanceRow {
  id: string;
}

function toStaffRow(r: StaffPerformanceRow): StaffRow {
  return { ...r, id: r.user_id };
}

// ─── Mobile Card ────────────────────────────────────────────────────────────

function StaffMobileCard({
  row,
  avgSales,
  showCommission,
  onClick,
}: {
  row: StaffRow;
  avgSales: number;
  showCommission: boolean;
  onClick: () => void;
}) {
  const currency = useTenantCurrency();
  const level = performanceLevel(row.total_sales, avgSales);
  const badge = PERF_BADGE[level];

  return (
    <div className={cn(GLASS_INTERACTIVE, 'p-4 animate-slide-up')} onClick={onClick}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
          {initials(row.user_name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-base font-semibold text-neutral-900 dark:text-neutral-100 truncate">{row.user_name}</p>
            <Badge variant={ROLE_BADGE_VARIANT[row.role] ?? 'draft'} size="sm">{row.role}</Badge>
          </div>
          <div className="flex items-center gap-3 mt-1.5">
            <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
              {formatPaise(row.total_sales, currency)}
            </span>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {row.bill_count} bills
            </span>
            <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
          </div>
        </div>
      </div>
      {showCommission && (
        <div className="mt-2 flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
          <span>Commission: <span className="font-bold font-mono tabular-nums text-violet-600 dark:text-violet-400">{formatPaise(row.commission_earned, currency)}</span></span>
          <span>Returns: {row.returns_count}</span>
        </div>
      )}
    </div>
  );
}

// ─── Drill-Down Detail Modal ────────────────────────────────────────────────

function StaffDetailModal({
  staffRow,
  dateRange,
  open,
  onClose,
  showCommission,
}: {
  staffRow: StaffRow | null;
  dateRange: { start: string; end: string };
  open: boolean;
  onClose: () => void;
  showCommission: boolean;
}) {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [activity, setActivity] = useState<StaffActivityEntry[]>([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(1);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);

  const [perf, setPerf] = useState<StaffPerformance | null>(null);
  const [isLoadingPerf, setIsLoadingPerf] = useState(false);

  useEffect(() => {
    if (!open || !staffRow?.user_id) return;
    setActivityPage(1);
    setPerf(null);
  }, [open, staffRow?.user_id]);

  // Fetch full per-user performance detail (hourly + top products) on open
  useEffect(() => {
    if (!open || !staffRow?.user_id) return;
    let cancelled = false;
    setIsLoadingPerf(true);
    staffService
      .getPerformance(staffRow.user_id, {
        start_date: dateRange.start,
        end_date: dateRange.end,
      })
      .then((res) => { if (!cancelled) setPerf(res); })
      .catch(() => { if (!cancelled) toast({ title: 'Failed to load performance detail', variant: 'error' }); })
      .finally(() => { if (!cancelled) setIsLoadingPerf(false); });
    return () => { cancelled = true; };
  }, [open, staffRow?.user_id, dateRange.start, dateRange.end, toast]);

  useEffect(() => {
    if (!open || !staffRow?.user_id) return;
    let cancelled = false;
    setIsLoadingActivity(true);
    staffService
      .getActivity(staffRow.user_id, {
        start_date: dateRange.start,
        end_date: dateRange.end,
        page: activityPage,
        limit: 10,
      })
      .then((res) => {
        if (!cancelled) {
          setActivity(res.data.entries);
          setActivityTotal(res.meta.total);
        }
      })
      .catch(() => {
        if (!cancelled) toast({ title: 'Failed to load activity log', variant: 'error' });
      })
      .finally(() => { if (!cancelled) setIsLoadingActivity(false); });
    return () => { cancelled = true; };
  }, [open, staffRow?.user_id, dateRange.start, dateRange.end, activityPage, toast]);

  if (!staffRow) return null;

  const violetTone = toneClasses('violet');
  const successTone = toneClasses('success');
  const warningTone = toneClasses('warning');
  const dangerTone = toneClasses('danger');
  const activityTotalPages = Math.ceil(activityTotal / 10);

  return (
    <Modal open={open} onClose={onClose} title="" variant="wide">
      <div className="max-h-[85vh] overflow-y-auto -mt-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white text-lg font-bold shrink-0">
            {initials(staffRow.user_name)}
          </div>
          <div>
            <h2 className="text-xl font-bold text-neutral-900 dark:text-white">{staffRow.user_name}</h2>
            <Badge variant={ROLE_BADGE_VARIANT[staffRow.role] ?? 'draft'} size="sm">{staffRow.role}</Badge>
          </div>
        </div>

        {isLoadingPerf ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={cn(GLASS, 'p-3')}>
                <Skeleton variant="text" width="50%" height={12} />
                <Skeleton variant="text" width="70%" height={20} />
              </div>
            ))}
          </div>
        ) : perf ? (
          <>
            {/* Mini KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className={cn(GLASS, 'p-3 relative overflow-hidden')}>
                <div className={cn('absolute inset-0 bg-gradient-to-br opacity-30 pointer-events-none', violetTone.accent)} />
                <p className="text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-0.5 relative">Total Sales</p>
                <p className="text-lg font-bold font-mono tabular-nums text-neutral-900 dark:text-white relative">{formatPaise(perf.total_sales, currency)}</p>
              </div>
              <div className={cn(GLASS, 'p-3 relative overflow-hidden')}>
                <div className={cn('absolute inset-0 bg-gradient-to-br opacity-30 pointer-events-none', successTone.accent)} />
                <p className="text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-0.5 relative">Bills</p>
                <p className="text-lg font-bold font-mono tabular-nums text-neutral-900 dark:text-white relative">{perf.bill_count.toLocaleString('en-IN')}</p>
              </div>
              <div className={cn(GLASS, 'p-3 relative overflow-hidden')}>
                <div className={cn('absolute inset-0 bg-gradient-to-br opacity-30 pointer-events-none', warningTone.accent)} />
                <p className="text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-0.5 relative">Avg Bill</p>
                <p className="text-lg font-bold font-mono tabular-nums text-neutral-900 dark:text-white relative">{formatPaise(perf.avg_bill_value, currency)}</p>
              </div>
              <div className={cn(GLASS, 'p-3 relative overflow-hidden')}>
                <div className={cn('absolute inset-0 bg-gradient-to-br opacity-30 pointer-events-none', dangerTone.accent)} />
                <p className="text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-0.5 relative">Returns</p>
                <p className="text-lg font-bold font-mono tabular-nums text-neutral-900 dark:text-white relative">{perf.returns_count}</p>
              </div>
            </div>

            {showCommission && (
              <div className={cn(GLASS, 'p-4 mb-6')}>
                <div className="flex items-center gap-3">
                  <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', violetTone.icon)}>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Commission Earned</p>
                    <p className="text-lg font-bold font-mono tabular-nums text-violet-700 dark:text-violet-300">{formatPaise(perf.commission_earned, currency)}</p>
                  </div>
                  <div className="ml-auto">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Discounts Given</p>
                    <p className="text-lg font-bold font-mono tabular-nums text-amber-700 dark:text-amber-300">{formatPaise(perf.discounts_given, currency)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Hourly Performance Chart */}
            {perf.hourly_performance && perf.hourly_performance.length > 0 && (
              <div className={cn(GLASS, 'p-5 mb-6')}>
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-4">
                  Hourly Performance
                </h3>
                <HourlyChart data={perf.hourly_performance} />
                <div className="flex justify-between text-[9px] text-neutral-400 dark:text-neutral-500 mt-1 px-1">
                  <span>12 AM</span>
                  <span>6 AM</span>
                  <span>12 PM</span>
                  <span>6 PM</span>
                  <span>11 PM</span>
                </div>
              </div>
            )}

            {/* Top Products */}
            {perf.top_products_sold && perf.top_products_sold.length > 0 && (
              <div className={cn(GLASS, 'p-5 mb-6 overflow-hidden')}>
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-3">
                  Top Products Sold
                </h3>
                <div className="space-y-2">
                  {perf.top_products_sold.slice(0, 10).map((p, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-bold text-neutral-400 dark:text-neutral-500 w-5">{i + 1}.</span>
                        <span className="text-sm text-neutral-800 dark:text-neutral-200 truncate">{p.product_name}</span>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="text-xs font-mono text-neutral-500 dark:text-neutral-400 tabular-nums">{p.quantity_sold} sold</span>
                        <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{formatPaise(p.revenue, currency)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
            <p>No performance data available for this period.</p>
          </div>
        )}

        {/* Activity Log */}
        <div className={cn(GLASS, 'p-5 overflow-hidden')}>
          <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-3">
            Activity Log
          </h3>
          {isLoadingActivity ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex gap-3">
                  <Skeleton variant="text" width="20%" height={14} />
                  <Skeleton variant="text" width="20%" height={14} />
                  <Skeleton variant="text" width="40%" height={14} />
                </div>
              ))}
            </div>
          ) : activity.length === 0 ? (
            <p className="text-sm text-neutral-400 dark:text-neutral-500 py-4 text-center">No activity recorded for this period.</p>
          ) : (
            <>
              <div className="space-y-2">
                {activity.map((a) => {
                  const full = a.path + (a.resource_id ? ` → ${a.resource_id}` : '');
                  const truncated = full.length > 140 ? full.slice(0, 140) + '…' : full;
                  return (
                    <div key={a.id} className="flex items-start gap-3 py-2 even:bg-neutral-50/50 dark:even:bg-neutral-800/30 rounded-lg px-2 -mx-2">
                      <span className="text-xs font-mono tabular-nums text-neutral-400 dark:text-neutral-500 shrink-0 pt-0.5">
                        {formatDateTime(a.created_at)}
                      </span>
                      <Badge variant="info" size="sm">{a.action}</Badge>
                      <span
                        className="text-sm text-neutral-700 dark:text-neutral-300 min-w-0 truncate"
                        title={full.length > 140 ? full : undefined}
                      >
                        {truncated}
                      </span>
                    </div>
                  );
                })}
              </div>
              {activityTotalPages > 1 && (
                <div className="mt-4">
                  <Pagination
                    currentPage={activityPage}
                    totalPages={activityTotalPages}
                    totalItems={activityTotal}
                    pageSize={10}
                    onPageChange={setActivityPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function StaffPerformanceContent() {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();

  // Role
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const showCommission = roleLevel >= ROLE_LEVELS.OWNER;

  // Date range (defaults to current month)
  const defaultRange = useMemo(() => getMonthRange(), []);
  const [startDate, setStartDate] = useState(searchParams.get('start_date') ?? defaultRange.start);
  const [endDate, setEndDate] = useState(searchParams.get('end_date') ?? defaultRange.end);

  // Filters
  const [roleFilter, setRoleFilter] = useState(searchParams.get('role') ?? '');
  const [locationFilter, setLocationFilter] = useState(searchParams.get('location_id') ?? '');
  const [page, setPage] = useState(Number(searchParams.get('page') ?? '1'));

  // Sync URL
  useEffect(() => {
    const params: Record<string, string> = {};
    if (startDate !== defaultRange.start) params.start_date = startDate;
    if (endDate !== defaultRange.end) params.end_date = endDate;
    if (roleFilter) params.role = roleFilter;
    if (locationFilter) params.location_id = locationFilter;
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [startDate, endDate, roleFilter, locationFilter, page, defaultRange, setSearchParams]);

  // Data — single /staff/performance call replaces the old N+1 pattern
  const [listData, setListData] = useState<StaffPerformanceListResponse | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const invalidRange = isInvalidDateRange(startDate, endDate);

  const fetchListPerformance = useCallback(async () => {
    if (isInvalidDateRange(startDate, endDate)) {
      // Don't fire a request for an inverted range — it would return no rows and
      // read as "no data" instead of "bad input". Surface a validation message.
      setListData(null);
      setTotalItems(0);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const result = await staffService.listPerformance({
        page,
        limit: PAGE_SIZE,
        role: roleFilter || undefined,
        location_id: locationFilter || undefined,
        start_date: startDate,
        end_date: endDate,
      });
      setListData(result.data);
      setTotalItems(result.meta.total);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load staff performance.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [page, roleFilter, locationFilter, startDate, endDate, toast]);

  useEffect(() => { fetchListPerformance(); }, [fetchListPerformance]);

  // Drill-down modal
  const [selectedStaff, setSelectedStaff] = useState<StaffRow | null>(null);

  // Build rows (server already sorts by total_sales desc)
  const staffRows: StaffRow[] = useMemo(
    () => (listData?.rows ?? []).map(toStaffRow),
    [listData],
  );

  // Aggregate stats — all tenant-wide (filtered window), independent of the
  // active page. The leaderboard/podium/distribution and the Top/Average/Low
  // badges must reflect whole-team standing, not the visible 20 rows. (#189)
  const aggregateStats = useMemo(() => {
    const summary = listData?.summary;

    const totalSales = summary?.total_sales ?? 0;
    const totalBills = summary?.bill_count ?? 0;
    const avgBill = summary?.avg_bill_value ?? 0;
    const activeStaff = summary?.active_staff ?? 0;
    const topPerformer = summary?.top_performer
      ? { name: summary.top_performer.user_name, sales: summary.top_performer.total_sales }
      : null;

    // Badge threshold — tenant-wide average per active staffer (stable across
    // pages/filters), NOT the visible page's average.
    const avgSalesPerStaff = teamAvgSalesPerStaff(totalSales, activeStaff);

    // Leaderboard — tenant-wide top 10 supplied by the server, not the current
    // page's rows. Map to StaffRow so the drill-down modal can open from a click
    // (the modal refetches full detail by user_id; per-row return/discount/
    // commission figures are not read from the row here).
    const ranked: StaffRow[] = (summary?.top_performers ?? []).map((e) => ({
      ...e,
      returns_count: 0,
      returns_value: 0,
      discounts_given: 0,
      commission_earned: 0,
      id: e.user_id,
    }));
    const maxRanked = ranked[0]?.total_sales ?? 0;

    return { totalStaff: totalItems, totalSales, totalBills, avgBill, activeStaff, topPerformer, avgSalesPerStaff, ranked, maxRanked };
  }, [listData, totalItems]);

  // Locations for filter
  const locations = useMemo(() => {
    const locs = profile?.locations ?? [];
    return [
      { value: '', label: 'All Locations' },
      ...locs.filter((l) => l.is_active).map((l) => ({ value: l.id, label: l.name })),
    ];
  }, [profile]);

  useEffect(() => { document.title = 'Staff Performance — Orio360 Admin'; }, []);

  // Table columns
  const columns: ColumnDef<StaffRow>[] = useMemo(() => {
    const cols: ColumnDef<StaffRow>[] = [
      {
        key: 'name', header: 'Staff',
        render: (_v: unknown, row: StaffRow) => (
          <button
            type="button"
            onClick={() => setSelectedStaff(row)}
            className="group flex items-center gap-3 -my-1 rounded-lg px-2 py-1 transition-colors hover:bg-violet-50 dark:hover:bg-violet-900/20"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
              {initials(row.user_name)}
            </div>
            <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 group-hover:text-violet-700 dark:group-hover:text-violet-300">{row.user_name}</span>
          </button>
        ),
      },
      {
        key: 'role', header: 'Role',
        render: (_v: unknown, row: StaffRow) => (
          <Badge variant={ROLE_BADGE_VARIANT[row.role] ?? 'draft'} size="sm">{row.role}</Badge>
        ),
      },
      {
        key: 'total_sales', header: 'Total Sales', numeric: true,
        render: (_v: unknown, row: StaffRow) => (
          <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
            {formatPaise(row.total_sales, currency)}
          </span>
        ),
      },
      {
        key: 'bills', header: 'Bills', numeric: true,
        render: (_v: unknown, row: StaffRow) => (
          <span className="text-sm font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
            {row.bill_count.toLocaleString('en-IN')}
          </span>
        ),
      },
      {
        key: 'avg_bill', header: 'Avg Bill', numeric: true,
        render: (_v: unknown, row: StaffRow) => (
          <span className="text-sm font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
            {formatPaise(row.avg_bill_value, currency)}
          </span>
        ),
      },
      {
        key: 'returns', header: 'Returns', numeric: true,
        render: (_v: unknown, row: StaffRow) => (
          <span className={cn('text-sm font-mono tabular-nums', row.returns_count > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-neutral-500 dark:text-neutral-400')}>
            {row.returns_count}
          </span>
        ),
      },
      {
        key: 'discounts', header: 'Discounts', numeric: true,
        render: (_v: unknown, row: StaffRow) => (
          <span className="text-sm font-mono tabular-nums text-amber-600 dark:text-amber-400">
            {formatPaise(row.discounts_given, currency)}
          </span>
        ),
      },
    ];

    if (showCommission) {
      cols.push({
        key: 'commission', header: 'Commission', numeric: true,
        render: (_v: unknown, row: StaffRow) => (
          <span className="text-sm font-bold font-mono tabular-nums text-violet-600 dark:text-violet-400">
            {formatPaise(row.commission_earned, currency)}
          </span>
        ),
      });
    }

    cols.push({
      key: 'perf_level', header: 'Performance',
      render: (_v: unknown, row: StaffRow) => {
        const level = performanceLevel(row.total_sales, aggregateStats.avgSalesPerStaff);
        const badge = PERF_BADGE[level];
        return <Badge variant={badge.variant} size="sm">{badge.label}</Badge>;
      },
    });

    return cols;
  }, [showCommission, aggregateStats.avgSalesPerStaff, currency]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  const brandTone = toneClasses('brand');
  const successTone = toneClasses('success');
  const tealTone = toneClasses('teal');
  const violetTone = toneClasses('violet');

  return (
    <PageContainer
      title="Staff Performance"
      subtitle="Team activity and sales performance dashboard"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Staff Performance' },
      ]}
    >
      <div className="animate-fade-in">
        {/* Header Gradient */}
        <div className="bg-gradient-to-r from-violet-600 to-pink-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(139,92,246,0.45)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
          <div className="relative">
            <h1 className="text-2xl font-bold text-white">Staff Performance</h1>
            <p className="text-violet-100 mt-1">Team activity and sales performance dashboard</p>
          </div>
        </div>

        {/* KPI Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden')}>
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', brandTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', brandTone.icon)}>
                  <UsersIcon className="h-5 w-5" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-neutral-500 dark:text-neutral-400">Total Staff</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-white">
                {isLoading ? '—' : aggregateStats.totalStaff.toLocaleString('en-IN')}
              </p>
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5">
                {isLoading ? '' : `${aggregateStats.activeStaff.toLocaleString('en-IN')} active this period`}
              </p>
            </div>
          </div>

          <div className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden')} style={{ animationDelay: '50ms' }}>
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', successTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', successTone.icon)}>
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-xs sm:text-sm font-medium text-neutral-500 dark:text-neutral-400">Total Sales</span>
              </div>
              <p className="text-xl sm:text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-white">
                {isLoading ? '—' : formatPaise(aggregateStats.totalSales, currency)}
              </p>
            </div>
          </div>

          <div className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden')} style={{ animationDelay: '100ms' }}>
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', tealTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', tealTone.icon)}>
                  <ChartBarIcon />
                </div>
                <span className="text-xs sm:text-sm font-medium text-neutral-500 dark:text-neutral-400">Avg Bill</span>
              </div>
              <p className="text-xl sm:text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-white">
                {isLoading ? '—' : formatPaise(aggregateStats.avgBill, currency)}
              </p>
            </div>
          </div>

          <div className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden')} style={{ animationDelay: '150ms' }}>
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', violetTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', violetTone.icon)}>
                  <TrophyIcon />
                </div>
                <span className="text-xs sm:text-sm font-medium text-neutral-500 dark:text-neutral-400">Top Performer</span>
              </div>
              {aggregateStats.topPerformer ? (
                <div>
                  <p className="text-sm font-bold text-neutral-900 dark:text-white truncate">{aggregateStats.topPerformer.name}</p>
                  <p className="text-xs font-mono tabular-nums text-violet-600 dark:text-violet-400">{formatPaise(aggregateStats.topPerformer.sales, currency)}</p>
                </div>
              ) : (
                <p className="text-2xl font-bold text-neutral-900 dark:text-white">{isLoading ? '—' : '—'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6')}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 shrink-0">From</label>
              <DatePicker
                value={startDate ? new Date(`${startDate}T00:00:00`) : undefined}
                onChange={(d: Date) => {
                  setStartDate(toLocalIsoDate(d));
                  setPage(1);
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 shrink-0">To</label>
              <DatePicker
                value={endDate ? new Date(`${endDate}T00:00:00`) : undefined}
                onChange={(d: Date) => {
                  setEndDate(toLocalIsoDate(d));
                  setPage(1);
                }}
              />
            </div>
            {locations.length > 2 && (
              <div className="w-44">
                <Select
                  options={locations}
                  value={locationFilter}
                  onChange={(val) => {
                    setLocationFilter(typeof val === 'string' ? val : val[0] ?? '');
                    setPage(1);
                  }}
                  placeholder="All Locations"
                />
              </div>
            )}
            <div className="w-40">
              <Select
                options={ROLE_FILTER_OPTIONS}
                value={roleFilter}
                onChange={(val) => {
                  setRoleFilter(typeof val === 'string' ? val : val[0] ?? '');
                  setPage(1);
                }}
                placeholder="All Roles"
              />
            </div>
          </div>
          {invalidRange && (
            <p className="mt-3 text-xs font-medium text-danger-600 dark:text-danger-400">
              End date must be on or after the start date.
            </p>
          )}
        </div>

        {invalidRange ? (
          <EmptyState
            variant="page"
            icon={<UsersEmptyIcon />}
            title="End date must be on or after the start date"
            description="The “To” date is earlier than the “From” date. Adjust the range to see staff performance."
          />
        ) : isLoading ? (
          <div className={cn(GLASS, 'p-6')}>
            <div className="space-y-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <Skeleton variant="circle" width={32} height={32} />
                  <Skeleton variant="text" width="15%" height={18} />
                  <Skeleton variant="rect" width={60} height={24} />
                  <Skeleton variant="text" width="12%" height={18} />
                  <Skeleton variant="text" width="8%" height={18} />
                  <Skeleton variant="text" width="10%" height={18} />
                  <Skeleton variant="text" width="8%" height={18} />
                  <Skeleton variant="text" width="10%" height={18} />
                </div>
              ))}
            </div>
          </div>
        ) : staffRows.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<UsersEmptyIcon />}
            title="No staff performance data"
            description="Performance data appears after staff complete billing shifts"
          />
        ) : (
          <>
            {/* Top Performers Leaderboard + Sales Distribution (rankings + chart) */}
            {aggregateStats.ranked.length > 0 && (
              <div className="hidden sm:grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                {/* Leaderboard — spans 2 cols on lg */}
                <div className={cn(GLASS, 'p-5 lg:col-span-2 animate-slide-up overflow-hidden')}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', toneClasses('violet').icon)}>
                        <TrophyIcon className="h-4 w-4" />
                      </div>
                      <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                        Top Performers
                      </h3>
                    </div>
                    <span className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">Top 10 · whole team</span>
                  </div>

                  {/* Podium — top 3 (only when we have ≥3) */}
                  {aggregateStats.ranked.length >= 3 && (
                    <div className="hidden md:grid grid-cols-3 gap-3 mb-5 items-end">
                      {[1, 0, 2].map((podiumIdx) => {
                        const p = aggregateStats.ranked[podiumIdx];
                        if (!p) return <div key={podiumIdx} />;
                        const tone = podiumIdx === 0 ? 'violet' : podiumIdx === 1 ? 'teal' : 'warning';
                        const t = toneClasses(tone);
                        const heightClass = podiumIdx === 0 ? 'pt-6' : podiumIdx === 1 ? 'pt-3' : 'pt-1';
                        const medal = podiumIdx === 0 ? '1' : podiumIdx === 1 ? '2' : '3';
                        const level = performanceLevel(p.total_sales, aggregateStats.avgSalesPerStaff);
                        const badge = PERF_BADGE[level];
                        return (
                          <button
                            key={p.user_id}
                            type="button"
                            onClick={() => setSelectedStaff(p)}
                            className={cn(
                              'relative rounded-2xl p-4 text-left transition-all duration-200 hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400',
                              heightClass,
                              'bg-gradient-to-br',
                              t.accent,
                            )}
                          >
                            <div className="absolute top-2 right-2">
                              <div className={cn('w-7 h-7 rounded-full flex items-center justify-center text-sm font-extrabold font-mono shadow-sm', t.icon)}>
                                {medal}
                              </div>
                            </div>
                            <div className="w-12 h-12 rounded-full bg-white/90 dark:bg-neutral-900/90 backdrop-blur-sm flex items-center justify-center text-sm font-bold text-neutral-800 dark:text-neutral-100 shadow-sm mb-3">
                              {initials(p.user_name)}
                            </div>
                            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate" title={p.user_name}>
                              {p.user_name}
                            </p>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                              {p.role}
                            </p>
                            <p className="text-base font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
                              {formatPaise(p.total_sales, currency)}
                            </p>
                            <div className="mt-1 flex items-center gap-2">
                              <span className="text-[11px] font-mono tabular-nums text-neutral-600 dark:text-neutral-300">
                                {p.bill_count.toLocaleString('en-IN')} bills
                              </span>
                              <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Ranks 4–10 (or full list if fewer than 3 total) */}
                  {(() => {
                    const rest = aggregateStats.ranked.length >= 3
                      ? aggregateStats.ranked.slice(3)
                      : aggregateStats.ranked;
                    const startIdx = aggregateStats.ranked.length >= 3 ? 4 : 1;
                    if (rest.length === 0) return null;
                    return (
                      <ol className="space-y-1">
                        {rest.map((p, idx) => {
                          const rank = startIdx + idx;
                          const level = performanceLevel(p.total_sales, aggregateStats.avgSalesPerStaff);
                          const badge = PERF_BADGE[level];
                          return (
                            <li key={p.user_id}>
                              <button
                                type="button"
                                onClick={() => setSelectedStaff(p)}
                                className="w-full flex items-center gap-3 py-2 px-3 -mx-1 rounded-lg text-left transition-colors even:bg-neutral-50/60 dark:even:bg-neutral-800/30 hover:bg-violet-50 dark:hover:bg-violet-900/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                              >
                                <span className="text-xs font-mono tabular-nums font-bold text-neutral-400 dark:text-neutral-500 w-6 text-center">
                                  {rank}
                                </span>
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                                  {initials(p.user_name)}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">{p.user_name}</p>
                                  <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                                    {p.bill_count.toLocaleString('en-IN')} bills · avg {formatPaise(p.avg_bill_value, currency)}
                                  </p>
                                </div>
                                <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
                                <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100 w-24 text-right">
                                  {formatPaise(p.total_sales, currency)}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ol>
                    );
                  })()}
                </div>

                {/* Sales Distribution — horizontal bars */}
                <div className={cn(GLASS, 'p-5 animate-slide-up overflow-hidden')} style={{ animationDelay: '80ms' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', toneClasses('teal').icon)}>
                      <ChartBarIcon className="h-4 w-4" />
                    </div>
                    <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                      Sales Distribution
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {aggregateStats.ranked.map((p) => {
                      const widthPct = aggregateStats.maxRanked > 0
                        ? Math.max((p.total_sales / aggregateStats.maxRanked) * 100, 2)
                        : 2;
                      return (
                        <div key={p.user_id}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-neutral-700 dark:text-neutral-300 truncate max-w-[55%]" title={p.user_name}>
                              {p.user_name.split(' ')[0]}
                            </span>
                            <span className="text-[11px] font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                              {formatPaise(p.total_sales, currency)}
                            </span>
                          </div>
                          <div className="h-2 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-teal-500 via-violet-500 to-pink-500 transition-all duration-500"
                              style={{ width: `${widthPct}%` }}
                              role="img"
                              aria-label={`${p.user_name}: ${formatPaise(p.total_sales, currency)}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Desktop DataTable */}
            <div className={cn(GLASS, 'overflow-hidden hidden md:block mb-6')}>
              <div className="px-5 py-4">
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                  Staff ({totalItems.toLocaleString('en-IN')})
                </h3>
              </div>
              <DataTable<StaffRow>
                columns={columns}
                data={staffRows}
              />
            </div>

            {/* Mobile Cards */}
            <div className="block md:hidden space-y-3 mb-6">
              <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider px-1">
                Staff ({totalItems.toLocaleString('en-IN')})
              </h3>
              {staffRows.map((row) => (
                <StaffMobileCard
                  key={row.user_id}
                  row={row}
                  avgSales={aggregateStats.avgSalesPerStaff}
                  showCommission={showCommission}
                  onClick={() => setSelectedStaff(row)}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mb-6">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Staff Detail Modal */}
      <StaffDetailModal
        staffRow={selectedStaff}
        dateRange={{ start: startDate, end: endDate }}
        open={!!selectedStaff}
        onClose={() => setSelectedStaff(null)}
        showCommission={showCommission}
      />
    </PageContainer>
  );
}

export function StaffPerformancePage() {
  return (
    <ErrorBoundary>
      <StaffPerformanceContent />
    </ErrorBoundary>
  );
}
